import React from "react";
import { Target, AlertTriangle, ShieldAlert, Globe, FileX, Skull } from "lucide-react";
import { useT } from "@/i18n";

export default function DealIntelligencePage() {
  const { lang } = useT();
  const C = lang === "en" ? CONTENT_EN : CONTENT_FR;
  return (
    <div data-testid="view-deal-intel" className="max-w-5xl">
      <Hero L={C.hero} />

      <Section eyebrow={C.s1.eyebrow} title={C.s1.title} index="01">
        <p className="italic text-[#D4AF37] text-lg border-l-2 border-[#D4AF37] pl-5">{C.s1.quote}</p>
        <p dangerouslySetInnerHTML={{ __html: C.s1.body }} />
      </Section>

      <Section eyebrow={C.s2.eyebrow} title={C.s2.title} index="02">
        <p dangerouslySetInnerHTML={{ __html: C.s2.intro }} />
        <h4 className="font-serif text-xl mt-8 text-white">{C.s2.signalsTitle}</h4>
        <div className="mt-6 space-y-3">
          {C.s2.signals.map((r, i) => (
            <div key={i} className="flex gap-4 border border-[#FF3B30]/20 bg-[#FF3B30]/5 p-5">
              <AlertTriangle className="h-5 w-5 text-[#FF3B30] shrink-0 mt-0.5" />
              <div>
                <div className="font-serif text-white text-base">{r.sig}</div>
                <div className="text-sm text-[#A1A1AA] mt-1">{r.detail}</div>
              </div>
            </div>
          ))}
        </div>

        <Callout icon={Target} title={C.s2.ruleTitle}>
          <p>{C.s2.ruleIntro}</p>
          <ol className="list-decimal pl-5 space-y-2 mt-3">
            {C.s2.ruleQs.map((q, i) => <li key={i} dangerouslySetInnerHTML={{ __html: q }} />)}
          </ol>
          <p className="mt-3">{C.s2.ruleOutro}</p>
        </Callout>
      </Section>

      <Section eyebrow={C.s3.eyebrow} title={C.s3.title} index="03">
        <p>{C.s3.intro}</p>
        {C.s3.docs.map((d, i) => (
          <DocumentBlock key={i} code={d.code} name={d.name} positive={d.positive} redFlags={d.redFlags} redFlagsLabel={C.s3.redFlagsLabel} />
        ))}
      </Section>

      <Section eyebrow={C.s4.eyebrow} title={C.s4.title} index="04">
        <p>{C.s4.intro}</p>
        <h4 className="font-serif text-xl mt-8 text-white">{C.s4.productsTitle}</h4>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          {C.s4.products.map((p) => <RiskBlock key={p.rank} rank={p.rank} title={p.title} body={p.body} />)}
        </div>
        <h4 className="font-serif text-xl mt-10 text-white">{C.s4.geoTitle}</h4>
        <div className="mt-5 space-y-3">
          {C.s4.geos.map((g) => (
            <div key={g.zone} className="border border-white/10 bg-[#0c0c0c] p-5 flex gap-5 items-start">
              <div className="text-2xl shrink-0">{g.flag}</div>
              <div>
                <div className="font-serif text-lg">{g.zone}</div>
                <div className="text-sm text-[#A1A1AA] mt-2">{g.body}</div>
              </div>
            </div>
          ))}
        </div>
        <Callout icon={Globe} title={C.s4.ruleTitle}>
          <span dangerouslySetInnerHTML={{ __html: C.s4.ruleBody }} />
        </Callout>
      </Section>

      <Section eyebrow={C.s5.eyebrow} title={C.s5.title} index="05">
        {C.s5.scams.map((s) => (
          <ScamCard key={s.num} num={s.num} title={s.title} body={s.body} escape={s.escape} escapeLabel={C.s5.escapeLabel} scamLabel={C.s5.scamLabel} />
        ))}
      </Section>
    </div>
  );
}

// ===========================================================================
// CONTENT — FR
// ===========================================================================
const CONTENT_FR = {
  hero: {
    label: "Stratégie · 01",
    italic: "Deal Intelligence",
    accent: "Filtering",
    body: "Comment reconnaître un faux seller, repérer les red flags dans un SCO/ICPO/FCO, décoder la géographie du risque, et éviter les quatre structures d'arnaque classiques.",
  },
  s1: {
    eyebrow: "Philosophie",
    title: "Le vrai problème n'est pas de trouver des deals",
    quote: "Le vrai problème, c'est de survivre au bruit.",
    body: `Dans le monde des commodités et du trading intermédiaire, <strong class="text-white">99 % des "opportunités"</strong> qui circulent sont soit des fakes, soit des deals morts depuis des mois recyclés en boucle par des chaînes de brokers qui ne savent même pas d'où ça vient. Le débutant court après les deals. Le professionnel <strong class="text-white">filtre</strong>.`,
  },
  s2: {
    eyebrow: "Méthode",
    title: "Comment repérer un faux seller en 2 minutes",
    intro: `Avant même de lire un document, observe le <strong class="text-white">comportement</strong>. Un vrai seller n'a pas besoin de toi. Il est sollicité en permanence. Sa communication reflète ça : sobre, directe, sans urgence artificielle.`,
    signalsTitle: "Signaux d'alerte immédiats",
    signals: [
      { sig: "Contact direct sur WhatsApp / Telegram sans contexte", detail: "Les vrais sellers ou leurs représentants légitimes ne prospectent pas des inconnus." },
      { sig: "Promesses de quantités illimitées", detail: "\"50 000 MT/mois\", \"open quantity\", \"we can supply as much as you need\". Aucun producteur sérieux ne parle ainsi — la production est planifiée, contractualisée, allouée." },
      { sig: "NDA exigé avant même de révéler l'origine du produit", detail: "Tactique pour te garder dans le flou et t'empêcher de vérifier." },
      { sig: "Email générique (Gmail / Yahoo / Hotmail)", detail: "Un vrai fournisseur a un domaine professionnel vérifiable." },
      { sig: "Demande de frais en amont", detail: "\"SGS inspection\", \"customs release\", \"bank charges\". Tout paiement demandé avant livraison = arnaque classique." },
      { sig: "Mandat introuvable", detail: "Il se dit \"mandaté\" mais ne produit pas de lettre de mandat signée, vérifiable auprès du mandant." },
      { sig: "Localisation incohérente", detail: "Un \"seller de pétrole brut nigérian\" basé en Roumanie qui écrit en anglais approximatif = fraude." },
    ],
    ruleTitle: "La règle des 2 minutes",
    ruleIntro: "Pose trois questions simples :",
    ruleQs: [
      "« Pouvez-vous me fournir le nom de l'entité détentrice du produit et son numéro d'enregistrement ? »",
      "« Quelle est la banque émettrice des transactions précédentes ? »",
      "« Votre principal peut-il faire une <em>Proof of Product</em> directement à mon acheteur ? »",
    ],
    ruleOutro: "Un vrai seller répond clairement. Un fake tourne en rond, invente des complications ou disparaît.",
  },
  s3: {
    eyebrow: "Documents",
    title: "Red flags dans un SCO, ICPO, FCO",
    intro: "Ces documents structurent les transactions de commodités. Ils sont aussi les outils préférés des escrocs parce qu'ils sont faciles à fabriquer et que beaucoup de gens n'ont jamais lu un vrai.",
    redFlagsLabel: "Red flags",
    docs: [
      {
        code: "SCO", name: "Soft Corporate Offer",
        positive: "Un SCO légitime est sobre : produit, spec technique, prix (CIF/FOB), port d'origine, termes de paiement, validité. C'est tout.",
        redFlags: [
          "Prix anormalement bas vs spot (Platts, Argus). Si le Brent = 87 $/bbl et on t'offre 62 $/bbl, c'est un piège.",
          "Conditions de paiement irréalistes : \"D/P 30 days after B/L\" sur une 1ère transaction entre inconnus.",
          "Specs copiées-collées de Wikipédia, sans précision d'origine ni n° de lot.",
          "Pas d'en-tête officiel, pas de numéro de référence interne, pas de cachet/signature identifiable.",
          "Validité \"open\" ou \"until cancelled\" — une vraie offre a une date d'expiration précise.",
        ],
      },
      {
        code: "ICPO", name: "Irrevocable Corporate Purchase Order",
        positive: `Censé démontrer le sérieux de l'acheteur. Beaucoup de brokers en font émettre par de faux acheteurs pour "réchauffer" un deal et le revendre.`,
        redFlags: [
          "Aucune banque facilitative mentionnée, ou une banque absente des correspondants SWIFT reconnus.",
          "L'acheteur préféré n'a aucune présence vérifiable en ligne, pas de registre, pas de dirigeant identifiable.",
          "Signataire avec titre flou (\"Senior Representative\", \"Authorized Agent\") sans précision juridique.",
        ],
      },
      {
        code: "FCO", name: "Full Corporate Offer",
        positive: "Censé représenter un engagement plus fort du seller. 80 % des FCO qui circulent sur Telegram/WhatsApp sont des recyclés.",
        redFlags: [
          "Arrive non sollicité dans un groupe WhatsApp, transmis par un \"broker intermédiaire\" qui ne connaît pas le seller.",
          "Coordonnées bancaires introuvables dans les registres officiels (BIS, SWIFT directory).",
          "Mise en page identique à d'autres FCO — même template, mêmes formulations, seuls les noms changent.",
        ],
      },
    ],
  },
  s4: {
    eyebrow: "Géographies",
    title: "Les pays et produits à vigilance maximale",
    intro: "Ce n'est pas une question de préjugé — c'est une réalité documentée par les services de compliance, les cabinets d'avocats spécialisés et les banques correspondantes.",
    productsTitle: "Produits les plus fraudés",
    products: [
      { rank: "01", title: "Pétrole brut & raffinés", body: "D2, JP54, EN590. Le secteur n°1 des arnaques intermédiaires. Volumes gigantesques, marges apparentes alléchantes, chaîne de vérification complexe — environnement parfait pour les fraudeurs." },
      { rank: "02", title: "Or & métaux précieux", body: "Dore bars, gold dust. Faux certificats d'essai, faux rapports SGS, export licenses fabriquées." },
      { rank: "03", title: "Agricommodities", body: "Blé, sucre ICUMSA 45, engrais (DAP, UREA). Moins spectaculaire mais très actif — routes Afrique-Europe et Amérique du Sud-Asie." },
    ],
    geoTitle: "Géographies à vigilance maximale",
    geos: [
      { flag: "🇳🇬🇬🇭🇨🇲", zone: "Nigeria, Ghana, Cameroun", body: "Pétrole et or. Ce ne sont pas des pays sans vrais deals (le Nigeria est producteur OPEP majeur), mais ils concentrent le plus grand nombre de faux sellers et faux mandats." },
      { flag: "🇦🇪", zone: "Dubaï", body: "Paradoxalement, Dubaï est un hub légitime ET un paradis pour structures offshore et sociétés coquilles. Exige toujours un numéro DCCI ou DIFC vérifié." },
      { flag: "🇹🇷🇷🇴", zone: "Turquie & Europe de l'Est", body: "Point de transit ou de \"reflagging\" de deals d'origine douteuse." },
      { flag: "🇲🇾🇸🇬", zone: "Malaisie & Singapour", body: "De faux trading houses se constituent facilement dans ces juridictions et paraissent crédibles." },
    ],
    ruleTitle: "La règle géographie",
    ruleBody: `La géographie n'exclut jamais un deal, mais elle <strong class="text-white">module ton niveau de vérification</strong>. Un seller nigérian légitime comprend que tu feras une due diligence approfondie — et il la facilite. Un fraudeur va se plaindre que tu "ne lui fais pas confiance".`,
  },
  s5: {
    eyebrow: "Typologies",
    title: "Les 4 structures de scam classiques",
    scamLabel: "Scam #",
    escapeLabel: "Comment s'en sortir",
    scams: [
      { num: "01", title: "La chaîne de brokers infinie (daisy chain)", body: "Schéma le plus répandu. Un deal (vrai ou prétendu) à la source est transmis de broker en broker. À chaque niveau, chacun ajoute sa commission espérée. Le deal arrive à un acheteur potentiel avec un prix 15–30 % au-dessus du marché, les informations sur le seller original perdues, et aucun intermédiaire ne peut livrer quoi que ce soit. Le deal est cliniquement mort mais continue à circuler.", escape: "Exige toujours un chain reduction — accès direct au seller ou à son mandataire légal. Si personne ne peut l'arranger, sors." },
      { num: "02", title: "Le faux mandat (fake mandate)", body: `Un individu se présente comme "mandaté exclusif" d'un producteur ou d'une entité gouvernementale. Il produit une lettre de mandat qui semble officielle. En réalité, soit le document est falsifié, soit la société qu'il représente est une coquille sans accès au produit.`, escape: "Demande confirmation directement à l'entité mandante, par canal indépendant (email corporate, appel téléphonique au numéro public). Ne te contente jamais d'un document transmis par le mandataire lui-même." },
      { num: "03", title: "L'avance de frais (advance fee fraud B2B)", body: `Version sophistiquée de l'arnaque nigériane. "Le deal est prêt, le produit est en port, mais il y a des frais de libération douanière / caution bancaire / frais d'inspection SGS à payer en amont". Montant "raisonnable" (2 000 à 50 000 $). Une fois payé, le contact disparaît ou invente un nouveau problème.`, escape: "Règle absolue : dans une transaction de commodités légitime, aucun frais n'est payé en amont avant que la documentation bancaire (LC, SBLC, BG) soit en place. Tout écart = red flag majeur." },
      { num: "04", title: "Le test shipment piégé", body: `On te propose un premier envoi à petit volume pour "établir la confiance". L'envoi arrive — mais produit de qualité inférieure, documents incomplets, ou frais imprévus qui bloquent la marchandise au port. L'objectif : te faire croire que la relation est réelle, puis demander des avances sur les volumes suivants.`, escape: `Traite le premier deal comme tous les autres. Mêmes documents, mêmes garanties, mêmes vérifications. Pas de "test" sans SBLC.` },
    ],
  },
};

// ===========================================================================
// CONTENT — EN
// ===========================================================================
const CONTENT_EN = {
  hero: {
    label: "Strategy · 01",
    italic: "Deal Intelligence",
    accent: "Filtering",
    body: "How to recognise a fake seller, spot red flags in an SCO/ICPO/FCO, decode the geography of risk, and avoid the four classic scam structures.",
  },
  s1: {
    eyebrow: "Philosophy",
    title: "The real problem isn't finding deals",
    quote: "The real problem is surviving the noise.",
    body: `In the world of commodities and intermediary trading, <strong class="text-white">99% of the "opportunities"</strong> in circulation are either fakes or months-dead deals recycled in loops by chains of brokers who don't even know where they came from. Beginners chase deals. Professionals <strong class="text-white">filter</strong>.`,
  },
  s2: {
    eyebrow: "Method",
    title: "How to spot a fake seller in 2 minutes",
    intro: `Before reading any document, watch the <strong class="text-white">behaviour</strong>. A real seller doesn't need you. They are constantly solicited. Their communication reflects that: sober, direct, no artificial urgency.`,
    signalsTitle: "Immediate warning signals",
    signals: [
      { sig: "Direct WhatsApp / Telegram contact with no context", detail: "Real sellers or their legitimate reps don't prospect strangers." },
      { sig: "Promises of unlimited quantities", detail: "\"50,000 MT/month\", \"open quantity\", \"we can supply as much as you need\". No serious producer talks like this — production is planned, contracted, allocated." },
      { sig: "NDA demanded before even revealing the product origin", detail: "Tactic to keep you in the dark and prevent verification." },
      { sig: "Generic email (Gmail / Yahoo / Hotmail)", detail: "A real supplier has a verifiable professional domain." },
      { sig: "Upfront fee request", detail: "\"SGS inspection\", \"customs release\", \"bank charges\". Any payment requested before delivery = classic scam." },
      { sig: "Untraceable mandate", detail: "They claim to be \"mandated\" but produce no signed mandate letter that can be verified with the mandator." },
      { sig: "Inconsistent location", detail: "A \"Nigerian crude oil seller\" based in Romania writing broken English = fraud." },
    ],
    ruleTitle: "The 2-minute rule",
    ruleIntro: "Ask three simple questions:",
    ruleQs: [
      `"Can you provide the name of the entity holding the product and its registration number?"`,
      `"Which is the issuing bank for the previous transactions?"`,
      `"Can your principal issue a <em>Proof of Product</em> directly to my buyer?"`,
    ],
    ruleOutro: "A real seller answers clearly. A fake stalls, invents complications, or disappears.",
  },
  s3: {
    eyebrow: "Documents",
    title: "Red flags in an SCO, ICPO, FCO",
    intro: "These documents structure commodity transactions. They are also the favourite tools of scammers because they are easy to fabricate and most people have never read a real one.",
    redFlagsLabel: "Red flags",
    docs: [
      {
        code: "SCO", name: "Soft Corporate Offer",
        positive: "A legitimate SCO is sober: product, technical spec, price (CIF/FOB), port of origin, payment terms, validity. That's it.",
        redFlags: [
          "Abnormally low price vs spot (Platts, Argus). If Brent is $87/bbl and you're offered $62/bbl, it's a trap.",
          "Unrealistic payment terms: \"D/P 30 days after B/L\" on a first transaction between strangers.",
          "Specs copy-pasted from Wikipedia, with no precision on origin or lot number.",
          "No official letterhead, no internal reference number, no identifiable stamp/signature.",
          "Validity \"open\" or \"until cancelled\" — a real offer has a precise expiry date.",
        ],
      },
      {
        code: "ICPO", name: "Irrevocable Corporate Purchase Order",
        positive: `Supposed to demonstrate the buyer's seriousness. Many brokers have these issued by fake buyers to "warm up" a deal and resell it.`,
        redFlags: [
          "No facilitating bank mentioned, or a bank absent from recognised SWIFT correspondents.",
          "The favoured buyer has no verifiable online presence, no registry, no identifiable director.",
          "Signatory with vague title (\"Senior Representative\", \"Authorized Agent\") with no legal precision.",
        ],
      },
      {
        code: "FCO", name: "Full Corporate Offer",
        positive: "Supposed to represent a stronger commitment from the seller. 80% of FCOs circulating on Telegram/WhatsApp are recycled.",
        redFlags: [
          "Arrives unsolicited in a WhatsApp group, forwarded by an \"intermediary broker\" who doesn't know the seller.",
          "Bank details cannot be found in official registries (BIS, SWIFT directory).",
          "Layout identical to other FCOs — same template, same wording, only names change.",
        ],
      },
    ],
  },
  s4: {
    eyebrow: "Geographies",
    title: "Highest-vigilance countries and products",
    intro: "This isn't prejudice — it's a reality documented by compliance teams, specialised law firms and correspondent banks.",
    productsTitle: "Most defrauded products",
    products: [
      { rank: "01", title: "Crude oil & refined products", body: "D2, JP54, EN590. The #1 sector for broker scams. Huge volumes, attractive headline margins, complex verification chain — the perfect environment for fraudsters." },
      { rank: "02", title: "Gold & precious metals", body: "Doré bars, gold dust. Fake assay certificates, fake SGS reports, fabricated export licenses." },
      { rank: "03", title: "Agri-commodities", body: "Wheat, ICUMSA 45 sugar, fertilisers (DAP, UREA). Less spectacular but very active — Africa-Europe and South America-Asia routes." },
    ],
    geoTitle: "Highest-vigilance geographies",
    geos: [
      { flag: "🇳🇬🇬🇭🇨🇲", zone: "Nigeria, Ghana, Cameroon", body: "Oil and gold. These are not countries without real deals (Nigeria is a major OPEC producer), but they concentrate the highest number of fake sellers and fake mandates." },
      { flag: "🇦🇪", zone: "Dubai", body: "Paradoxically, Dubai is a legitimate hub AND a paradise for offshore structures and shell companies. Always require a verified DCCI or DIFC number." },
      { flag: "🇹🇷🇷🇴", zone: "Turkey & Eastern Europe", body: "Transit or \"reflagging\" point for deals of dubious origin." },
      { flag: "🇲🇾🇸🇬", zone: "Malaysia & Singapore", body: "Fake trading houses are easily set up in these jurisdictions and look credible." },
    ],
    ruleTitle: "The geography rule",
    ruleBody: `Geography never excludes a deal, but it <strong class="text-white">modulates your verification level</strong>. A legitimate Nigerian seller understands you'll run thorough due diligence — and facilitates it. A fraudster will complain you "don't trust them".`,
  },
  s5: {
    eyebrow: "Typology",
    title: "The 4 classic scam structures",
    scamLabel: "Scam #",
    escapeLabel: "How to exit",
    scams: [
      { num: "01", title: "The infinite broker chain (daisy chain)", body: "The most widespread scheme. A deal (real or alleged) at the source is passed from broker to broker. At each level, each one adds their hoped-for commission. The deal lands at a potential buyer with a price 15–30% above market, original seller info lost, and no intermediary can deliver anything. The deal is clinically dead but keeps circulating.", escape: "Always demand chain reduction — direct access to the seller or their legal mandator. If no one can arrange it, walk." },
      { num: "02", title: "The fake mandate", body: `An individual presents themselves as the "exclusive mandate" of a producer or government entity. They produce an apparently official mandate letter. In reality, the document is forged, or the company they represent is a shell with no product access.`, escape: "Ask for confirmation directly from the mandating entity, through an independent channel (corporate email, phone call to the public number). Never settle for a document forwarded by the mandate-holder themselves." },
      { num: "03", title: "Advance fee fraud (B2B)", body: `Sophisticated version of the Nigerian scam. "The deal is ready, the product is at port, but there are customs release fees / a bank guarantee / SGS inspection fees to pay upfront." "Reasonable" amount ($2,000 to $50,000). Once paid, the contact vanishes or invents a new problem.`, escape: "Absolute rule: in a legitimate commodity transaction, no fee is paid upfront before the bank documentation (LC, SBLC, BG) is in place. Any deviation = major red flag." },
      { num: "04", title: "The booby-trapped test shipment", body: `You're offered a small first shipment to "build trust". The shipment arrives — but with sub-quality product, incomplete documents, or unforeseen fees blocking the goods at port. The goal: make you believe the relationship is real, then ask for advances on subsequent volumes.`, escape: `Treat the first deal like all the others. Same documents, same guarantees, same checks. No "test" without SBLC.` },
    ],
  },
};

// ===========================================================================
// Layout subcomponents
// ===========================================================================
const Hero = ({ L }) => (
  <section className="relative mb-16 border border-white/10 bg-[#0c0c0c] overflow-hidden">
    <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(212,175,55,0.12),transparent_60%)]" />
    <div className="relative p-10 md:p-14">
      <div className="flex items-center gap-3">
        <Target className="h-5 w-5 text-[#D4AF37]" />
        <span className="text-[11px] uppercase tracking-[0.4em] text-[#D4AF37]">{L.label}</span>
      </div>
      <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light leading-tight mt-6 max-w-3xl">
        <em className="italic text-[#F5F5DC]">{L.italic}</em><br />&amp; <span className="text-[#D4AF37]">{L.accent}</span>.
      </h1>
      <p className="mt-6 max-w-2xl text-[#A1A1AA] leading-relaxed">{L.body}</p>
    </div>
  </section>
);

const Section = ({ eyebrow, title, index, children }) => (
  <section className="mb-20">
    <div className="flex items-baseline gap-6 mb-8">
      <span className="font-serif text-xl text-[#D4AF37] tabular-nums">{index}</span>
      <div>
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]">{eyebrow}</div>
        <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-light mt-2 leading-tight">{title}</h2>
      </div>
    </div>
    <div className="text-[#C4C4C7] leading-relaxed space-y-4 pl-0 md:pl-10">{children}</div>
  </section>
);

const Callout = ({ icon: Icon, title, children }) => (
  <div className="mt-10 border-l-2 border-[#D4AF37] bg-[#141414] p-6 md:p-8">
    <div className="flex items-center gap-3">
      <Icon className="h-4 w-4 text-[#D4AF37]" />
      <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{title}</span>
    </div>
    <div className="mt-4 text-[#E4E4E7] leading-relaxed text-sm">{children}</div>
  </div>
);

const DocumentBlock = ({ code, name, positive, redFlags, redFlagsLabel }) => (
  <div className="mt-8 border border-white/10 bg-[#0c0c0c] p-6 md:p-8">
    <div className="flex items-baseline gap-4">
      <FileX className="h-5 w-5 text-[#D4AF37]" />
      <span className="font-mono text-[#D4AF37] text-lg">{code}</span>
      <span className="font-serif text-xl text-white">— {name}</span>
    </div>
    <p className="mt-4 text-sm text-[#C4C4C7]">{positive}</p>
    <div className="mt-6 pt-6 border-t border-white/10">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-4">{redFlagsLabel}</div>
      <ul className="space-y-2 text-sm">
        {redFlags.map((r, i) => (
          <li key={i} className="flex gap-3 text-[#E4E4E7]">
            <span className="text-[#FF3B30] shrink-0">▸</span><span>{r}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const RiskBlock = ({ rank, title, body }) => (
  <div className="border border-white/10 bg-[#0c0c0c] p-5 hover:border-[#D4AF37]/30 transition-colors">
    <div className="font-serif text-2xl text-[#D4AF37]">#{rank}</div>
    <div className="font-serif text-lg mt-3">{title}</div>
    <p className="text-sm text-[#A1A1AA] mt-3 leading-relaxed">{body}</p>
  </div>
);

const ScamCard = ({ num, title, body, escape, escapeLabel, scamLabel }) => (
  <div className="mt-6 border border-white/10 bg-[#0c0c0c] p-7 md:p-8">
    <div className="flex items-center gap-4">
      <Skull className="h-5 w-5 text-[#D4AF37]" />
      <span className="font-mono text-[#D4AF37]">{scamLabel}{num}</span>
    </div>
    <h3 className="font-serif text-2xl mt-4 text-white">{title}</h3>
    <p className="mt-4 text-sm text-[#A1A1AA] leading-relaxed">{body}</p>
    <div className="mt-5 pt-5 border-t border-[#D4AF37]/30">
      <div className="flex items-start gap-3">
        <ShieldAlert className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{escapeLabel}</div>
          <p className="text-sm text-[#E4E4E7] mt-2">{escape}</p>
        </div>
      </div>
    </div>
  </div>
);
